import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateTemplateRequest,
  UpdateTemplateRequest,
  templatesService,
} from "../../../api/services/operations-service";
import { operationsKeys } from "./use-operations-queries";

export const templateKeys = {
  all: ["templates"] as const,
  lists: () => [...templateKeys.all, "list"] as const,
  list: () => [...templateKeys.lists()] as const,
};

export const useTemplates = () => {
  return useQuery({
    queryKey: templateKeys.list(),
    queryFn: async () => {
      const response = await templatesService.listTemplates();
      return response.data?.templates || [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
};

export const useCreateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTemplateRequest) =>
      templatesService.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.list() });
    },
  });
};

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateTemplateRequest) =>
      templatesService.updateTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.list() });
      // Operations that use this template may have stale screens/uploads
      queryClient.invalidateQueries({ queryKey: operationsKeys.all });
    },
  });
};

export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => templatesService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.list() });
      // Operations that referenced this template need to be refreshed
      queryClient.invalidateQueries({ queryKey: operationsKeys.all });
    },
  });
};
